import React from "react";
import { SearchField, Input, Label } from "react-aria-components";
import type { Key } from "@react-types/shared";

import Button from "./forms/button";
import Icon from "./icon";
import { PalletLangContext } from "../lang-provider";

import styles from "./search.module.css";

interface SearchProps {
  value: string;
  label?: string;
  "aria-label"?: string;
  autofocus?: boolean;
  placeholder?: string;
  onChange: (id: Key) => void;
  onClear: (id: Key) => void;
  isDisabled?: boolean;
  // NO className, and no width prop either. `Search` fills its container and
  // owns no width of its own (SR-301 phase 6, OQ-4): a call site that wants a
  // 280px search wraps this in a div and puts the 280px there, where layout
  // already lives. That keeps phase 4's D4 enforce-by-absence without
  // inventing a sizing API on a text field.
}

function noop() {}

function Search({
  value,
  label,
  autofocus = false,
  placeholder = "",
  onChange,
  onClear,
  isDisabled = false,
  "aria-label": ariaLabel,
}: SearchProps) {
  const langContext = React.useContext(PalletLangContext);
  const clearLabel =
    langContext?.simpleTranslate("actions.clearSearch") ?? "Clear search";

  const ariaLabelProps = ariaLabel ? { "aria-label": ariaLabel } : {};
  return (
    <SearchField
      {...ariaLabelProps}
      isDisabled={isDisabled}
      data-testid="pallet-search"
      className={styles.textField}
    >
      {label && (
        <Label data-testid="pallet-search-label" className={styles.label}>
          {label}
        </Label>
      )}
      {/* SR-342 item 6: the magnifying glass every search bar was missing.
          Decorative — `Icon`'s default `aria-hidden` arm is right here,
          because `SearchField`/`Input` already carry the field's name and a
          second announcement of "search" would only be noise. It is wrapped
          because `Icon` owns no className (phase 4 D4), so the one piece of
          layout it needs — sitting *over* the field's leading edge, the way
          the clear button sits over the trailing one — belongs to the row. */}
      <span className={styles.searchIcon} aria-hidden="true">
        <Icon name="Search" size={18} data-testid="pallet-search-icon" />
      </span>
      <Input
        {...ariaLabelProps}
        data-testid="pallet-search-input"
        value={value}
        onChange={(ev) => (onChange ? onChange(ev.target.value) : noop())}
        autoComplete="off"
        placeholder={placeholder}
        title={placeholder || undefined}
        autoFocus={autofocus}
        className={styles.input}
      />
      {/* Pallet's own Button, not a raw RAC one wrapping the glyph `✕`: the
          glyph was the button's entire content, and only react-aria's
          SearchField slot was supplying a name for it. An icon-only Button
          states the name outright, and `SearchField` still recognises it as
          its clear slot (hence the disabled-with-the-field behaviour). */}
      <Button
        kind="icon-only"
        icon="X"
        variant="bare"
        label={clearLabel}
        onPress={() => onClear("")}
      />
    </SearchField>
  );
}

export default Search;
